/**
 * URL Parameter Extraction Utility
 *
 * Extracts and validates Learnworlds authentication parameters from URL.
 * Provides type-safe parameter extraction with validation.
 *
 * [A006] Implementation
 */

/**
 * Learnworlds authentication parameters structure
 */
export interface LearnworldsAuthParams {
  userId: string
  email: string
  name?: string
}

/**
 * Result of parameter extraction
 */
export interface ParamExtractionResult {
  success: boolean
  params?: LearnworldsAuthParams
  errors?: string[]
}

/**
 * Extract authentication parameters from current URL
 *
 * @returns Authentication parameters if present
 *
 * @example
 * // URL: /games/cards?userId=lw_123&email=user@example.com&name=John
 * const { userId, email, name } = extractAuthParams()
 * console.log(userId) // 'lw_123'
 */
export function extractAuthParams(): Partial<LearnworldsAuthParams> {
  const params = new URLSearchParams(window.location.search)

  return {
    userId: params.get('userId') || undefined,
    email: params.get('email') || undefined,
    name: params.get('name') || undefined,
  }
}

/**
 * Extract authentication parameters from a specific URL string
 *
 * @param url - Full URL or query string to extract from
 * @returns Authentication parameters if present
 *
 * @example
 * const url = 'https://example.com/game?userId=123&email=user@example.com'
 * const { userId, email } = extractAuthParamsFromUrl(url)
 */
export function extractAuthParamsFromUrl(url: string): Partial<LearnworldsAuthParams> {
  try {
    // Handle both full URLs and query strings
    const urlObj = url.startsWith('http') ? new URL(url) : new URL(`http://dummy${url}`)
    const params = new URLSearchParams(urlObj.search)

    return {
      userId: params.get('userId') || undefined,
      email: params.get('email') || undefined,
      name: params.get('name') || undefined,
    }
  } catch (error) {
    console.error('[URL Params] Failed to parse URL:', error)
    return {}
  }
}

/**
 * Validate extracted authentication parameters
 *
 * @param params - Partial authentication parameters to validate
 * @returns Validation result with errors if invalid
 *
 * @example
 * const params = extractAuthParams()
 * const result = validateAuthParams(params)
 * if (!result.success) {
 *   console.error('Validation errors:', result.errors)
 * }
 */
export function validateAuthParams(
  params: Partial<LearnworldsAuthParams>
): ParamExtractionResult {
  const errors: string[] = []

  // Check required fields
  if (!params.userId || params.userId.trim().length === 0) {
    errors.push('userId parameter is required')
  } else if (params.userId.length > 100) {
    errors.push('userId parameter exceeds maximum length (100 characters)')
  }

  if (!params.email || params.email.trim().length === 0) {
    errors.push('email parameter is required')
  } else {
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(params.email)) {
      errors.push('email parameter has invalid format')
    } else if (params.email.length > 254) {
      errors.push('email parameter exceeds maximum length (254 characters)')
    }
  }

  // Validate optional name
  if (params.name !== undefined && params.name.length > 200) {
    errors.push('name parameter exceeds maximum length (200 characters)')
  }

  if (errors.length > 0) {
    return {
      success: false,
      errors,
    }
  }

  return {
    success: true,
    params: {
      userId: params.userId!.trim(),
      email: params.email!.trim().toLowerCase(),
      name: params.name?.trim(),
    },
  }
}

/**
 * Extract and validate authentication parameters in one call
 *
 * @returns Validation result with extracted parameters or errors
 *
 * @example
 * const result = extractAndValidateAuthParams()
 * if (result.success) {
 *   const { userId, email, name } = result.params!
 *   // Proceed with authentication
 * } else {
 *   console.error('Invalid auth params:', result.errors)
 * }
 */
export function extractAndValidateAuthParams(): ParamExtractionResult {
  const params = extractAuthParams()
  return validateAuthParams(params)
}

/**
 * Check if authentication parameters are present in URL
 *
 * @returns true if userId and email parameters exist
 *
 * @example
 * if (!hasAuthParams()) {
 *   console.error('Authentication parameters missing from URL')
 * }
 */
export function hasAuthParams(): boolean {
  const params = extractAuthParams()
  return Boolean(params.userId && params.email)
}

/**
 * Build URL with authentication parameters
 *
 * @param basePath - Base path or URL
 * @param authParams - Authentication parameters to add
 * @returns Full URL with authentication parameters
 *
 * @example
 * const url = buildAuthUrl('/games/cards', {
 *   userId: 'lw_123',
 *   email: 'user@example.com',
 *   name: 'John Doe'
 * })
 * // Returns: '/games/cards?userId=lw_123&email=user@example.com&name=John+Doe'
 */
export function buildAuthUrl(basePath: string, authParams: LearnworldsAuthParams): string {
  const params = new URLSearchParams({
    userId: authParams.userId,
    email: authParams.email,
  })

  if (authParams.name) {
    params.set('name', authParams.name)
  }

  return `${basePath}?${params.toString()}`
}

/**
 * Sanitize authentication parameters for logging
 * Masks sensitive data (email) to prevent PII leakage
 *
 * @param params - Authentication parameters to sanitize
 * @returns Sanitized version safe for logging
 *
 * @example
 * const params = extractAuthParams()
 * console.log('Auth params:', sanitizeParamsForLogging(params))
 * // Output: { userId: 'lw_123', email: 'u***@example.com', name: 'John Doe' }
 */
export function sanitizeParamsForLogging(
  params: Partial<LearnworldsAuthParams>
): Record<string, string | undefined> {
  return {
    userId: params.userId,
    email: params.email ? maskEmail(params.email) : undefined,
    name: params.name,
  }
}

/**
 * Mask email address for safe logging
 * Example: user@example.com -> u***@example.com
 */
function maskEmail(email: string): string {
  const [username, domain] = email.split('@')
  if (!username || !domain) return '***@***'

  const maskedUsername =
    username.length > 1 ? username[0] + '*'.repeat(Math.min(username.length - 1, 3)) : username

  return `${maskedUsername}@${domain}`
}

/**
 * Remove authentication parameters from URL
 * Useful for cleaning up URL after authentication is complete
 *
 * @example
 * removeAuthParamsFromUrl()
 * // URL changes from /games/cards?userId=123&email=user@example.com
 * // to /games/cards
 */
export function removeAuthParamsFromUrl(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete('userId')
  url.searchParams.delete('email')
  url.searchParams.delete('name')

  // Update URL without page reload
  window.history.replaceState({}, '', url.toString())
}
